import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL, getErrorMessage } from '../types';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';

interface ClosedBetaProps {
  inline?: boolean;
  onLogout?: () => void;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export const ClosedBeta: React.FC<ClosedBetaProps> = ({
  inline,
  onLogout,
  email,
  firstName,
  lastName,
}) => {
  const { t } = useTranslation();
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const canJoinWaitlist = Boolean(email && firstName && lastName);

  const { loading: joiningWaitlist, call: joinWaitlist } = useApiRequest(async () => {
    await axios.post(`${API_URL}/auth/beta-waitlist`, {
      email,
      firstName,
      lastName,
    });
    setWaitlistJoined(true);
    setWaitlistError('');
  });

  const handleJoinWaitlist = useCallback(async () => {
    if (!canJoinWaitlist) return;
    setWaitlistError('');
    try {
      await joinWaitlist();
    } catch (err) {
      setWaitlistError(getErrorMessage(err, t('closed_beta.waitlist_error')));
    }
  }, [canJoinWaitlist, joinWaitlist, t]);

  const content = (
    <>
      <h2 style={inline ? { margin: '0 0 0.25rem' } : undefined}>{t('closed_beta.title')}</h2>
      <p className="login-subtitle">{t('closed_beta.text')}</p>
      {email ? (
        <p className="login-subtitle">{t('closed_beta.not_whitelisted', { email })}</p>
      ) : (
        <p className="login-subtitle">{t('closed_beta.not_whitelisted_generic')}</p>
      )}
      {waitlistJoined ? (
        <div className="alert alert-success" style={{ marginTop: '1rem' }}>
          {t('closed_beta.waitlist_success')}
        </div>
      ) : canJoinWaitlist ? (
        <div style={{ marginTop: '1rem' }}>
          <p className="login-subtitle">{t('closed_beta.waitlist_prompt')}</p>
          {waitlistError && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {waitlistError}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={joiningWaitlist}
            onClick={handleJoinWaitlist}
          >
            {joiningWaitlist && <span className="btn-spinner" aria-hidden="true" />}
            {t('closed_beta.waitlist_join')}
          </button>
        </div>
      ) : (
        <p className="login-subtitle" style={{ marginTop: '1rem' }}>
          {t('closed_beta.contact')}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}>
        <span style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('closed_beta.follow')}
        </span>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <a href="https://www.facebook.com/profile.php?id=61589109270617" aria-label="Facebook" className="social-icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
          </a>
          <a href="https://www.instagram.com/rondoshare/" aria-label="Instagram" className="social-icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.17.054 1.97.24 2.43.403a4.088 4.088 0 011.47.957c.453.453.774.898.957 1.47.163.46.349 1.26.404 2.43.058 1.265.069 1.645.069 4.849s-.012 3.584-.07 4.85c-.054 1.17-.24 1.97-.403 2.43a4.088 4.088 0 01-.957 1.47 4.088 4.088 0 01-1.47.957c-.46.163-1.26.349-2.43.404-1.265.058-1.645.069-4.849.069s-3.584-.012-4.85-.07c-1.17-.054-1.97-.24-2.43-.403a4.088 4.088 0 01-1.47-.957 4.088 4.088 0 01-.957-1.47c-.163-.46-.349-1.26-.404-2.43C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.054-1.17.24-1.97.403-2.43a4.088 4.088 0 01.957-1.47A4.088 4.088 0 015.063 2.293c.46-.163 1.26-.349 2.43-.404C8.758 1.831 9.138 1.82 12 1.82V2.163zM12 6.865a5.135 5.135 0 100 10.27 5.135 5.135 0 000-10.27zm0 8.468a3.333 3.333 0 110-6.666 3.333 3.333 0 010 6.666zm5.338-8.671a1.2 1.2 0 11-2.4 0 1.2 1.2 0 012.4 0z"/></svg>
          </a>
        </div>
      </div>
      {onLogout && (
        <div className="form-actions" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
          <button type="button" className="btn btn-secondary" onClick={onLogout}>
            {t('nav.logout')}
          </button>
        </div>
      )}
    </>
  );

  if (inline) return content;

  return (
    <div className="login-page">
      <div className="login-card">{content}</div>
    </div>
  );
};
